import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { Select , Checkbox} from '../components/UI';
import { useStore, useFieldOptions, fetchAllPaginated } from '../data/store';
import supabase from '../data/supabase';
import { IMPORT_FIELDS, autoMapColumns } from '../data/importMapper';
import { matchesFilter, SEG_FIELDS, SEG_MAP } from '../data/listFilters';

/* ═══════════════════════════════════════════════════════
   LISTS & SEGMENTS — Supabase-backed, multi-org hardened
   ═══════════════════════════════════════════════════════ */

// Standard truncation triple — applied to every text-bearing <td> in the
// contacts table inside Lists so paragraph-length values (long names,
// multi-line company strings) clip to one line with ellipsis instead of
// inflating row heights. Display-only — DB values untouched, accessible
// via the contact record page on click.
const truncStyle = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

// SEG_FIELDS, SEG_MAP, and matchesFilter are imported from src/data/listFilters.js
// (extracted to a shared module so the People page's Lists multi-select filter
// can evaluate smart-list predicates without duplicating logic).

// ── CSV parser (RFC 4180: handles multi-line quoted fields, commas, escaped quotes) ──
// Single-pass character walker that maintains quote state across newlines.
// Previous implementation split text on /\r?\n/ BEFORE walking quote-aware,
// which fragmented multi-line quoted cells (Apollo SEO Description,
// Technologies, etc.) into ghost rows whose continuation lines landed in
// column 0 (first_name). 30 such ghost rows were deleted from production
// before this commit.
function parseCSV(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const rows = [];
  let row = [];
  let cur = "";
  let inQ = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const ch = text[i];

    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQ = false; i++; continue;
      }
      cur += ch; i++; continue;
    }

    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ',') { row.push(cur.trim()); cur = ""; i++; continue; }
    if (ch === '\r') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      if (text[i + 1] === '\n') i += 2; else i++;
      continue;
    }
    if (ch === '\n') {
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
      i++; continue;
    }
    cur += ch; i++;
  }

  if (cur.length > 0 || row.length > 0) {
    row.push(cur.trim());
    rows.push(row);
  }

  const nonEmpty = rows.filter(r => r.some(c => c !== ""));
  if (nonEmpty.length < 2) return { headers: [], rows: [] };

  return { headers: nonEmpty[0], rows: nonEmpty.slice(1) };
}

// Auto-mapping uses the shared scored bipartite mapper from
// src/data/importMapper.js — replaces the local first-substring-wins
// suggestMapping() and IMP_FIELDS that auto-mapped "Company Size" to the
// Company field. Field ids in this file mirror the shared module.

// ── Export field definitions ──
const EXP_FIELDS = [
  { id: "fn", label: "First Name" }, { id: "ln", label: "Last Name" },
  { id: "email", label: "Email" }, { id: "phone", label: "Phone" },
  { id: "company", label: "Company" }, { id: "title", label: "Job Title" },
  { id: "source", label: "Lead Source" }, { id: "status", label: "Status" },
  { id: "score", label: "Score" }, { id: "industry", label: "Industry" },
  { id: "city", label: "City" }, { id: "state", label: "State" },
  { id: "tags", label: "Tags" }, { id: "leadStage", label: "Lead Stage" },
  { id: "created", label: "Created Date" },
];

function getVal(c, fId) {
  if (fId.startsWith("custom_")) { const k = fId.replace("custom_", ""); return String(c[k] ?? c.properties?.[k] ?? ""); }
  switch (fId) {
    case "fn": return c.fn || ""; case "ln": return c.ln || ""; case "email": return c.email || "";
    case "phone": return c.ph || c.phone || ""; case "company": return c.company || c.co || "";
    case "title": return c.title || ""; case "source": return c.source || ""; case "status": return c.status || "";
    case "score": return String(c.score || 0); case "industry": return c.industry || "";
    case "city": return c.city || ""; case "state": return c.state || "";
    case "tags": return (c.tags || []).join("; "); case "leadStage": return c.leadStage || "";
    case "created": return c.created || ""; default: return "";
  }
}

function downloadCSV(rows, fields, filename) {
  if (!fields.length || !rows.length) return;
  const esc = v => (v.includes(",") || v.includes('"') || v.includes("\n")) ? `"${v.replace(/"/g, '""')}"` : v;
  const csv = [fields.map(f => f.label).join(","), ...rows.map(c => fields.map(f => esc(getVal(c, f.id))).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename || "contacts-export.csv"; a.click();
  URL.revokeObjectURL(url);
}

const MAX_IMPORT_ROWS = 10000;

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════
export default function ListsPage({ onSelectContact, isMobile }) {
  const store = useStore();
  const { contacts, createContact, customFields, updateContact, currentUserId, currentOrgId } = store;
  const orgId = currentOrgId || window.__coreflow_org_id;
  const fileRef = useRef(null);
  const importingRef = useRef(false); // Guard against double import

  // ── Dynamic fields (custom properties) ──
  const ccf = useMemo(() => (customFields?.contacts || []).filter(f => f.type !== "calculation"), [customFields]);

  // Tier B options — reactive to admin-saved overrides. Overlay onto the
  // module-level SEG_FIELDS skeleton so segment filter UIs see live options.
  const statusOptions = useFieldOptions("contacts", "status");
  const sourceOptions = useFieldOptions("contacts", "source");
  const dynSegFields = useMemo(() => SEG_FIELDS.map(f => {
    if (f.id === "status") return { ...f, options: statusOptions };
    if (f.id === "source") return { ...f, options: sourceOptions };
    return f;
  }), [statusOptions, sourceOptions]);

  // Dynamic segmentation fields including custom fields from all objects
  const allSegFields = useMemo(() => {
    const custom = [];
    ["contacts", "deals", "companies", "tasks", "calls", "emails", "sms", "calendar"].forEach(entity => {
      (customFields?.[entity] || []).filter(f => f.type !== "calculation").forEach(f => {
        custom.push({
          id: `cf__${entity}__${f.id}`,
          label: `${f.label} (${entity})`,
          type: f.type === "select" ? "select" : f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : f.type === "checkbox" ? "boolean" : "text",
          options: f.options || [],
          _entity: entity,
          _fieldId: f.id,
        });
      });
    });
    return [...dynSegFields, ...custom];
  }, [customFields, dynSegFields]);
  const allImp = useMemo(() => [
    ...IMPORT_FIELDS,
    ...ccf.map(f => ({ id: "custom_" + f.id, label: f.label + " ⚙", aliases: [] })),
    { id: "skip", label: "— Skip —", aliases: [] },
  ], [ccf]);
  const allExp = useMemo(() => [
    ...EXP_FIELDS,
    ...ccf.map(f => ({ id: "custom_" + f.id, label: f.label })),
  ], [ccf]);

  // ── Core state ──
  const [lists, setLists] = useState([]);
  const [members, setMembers] = useState({});     // { listId: [contactId, ...] }
  const [activeList, setActiveList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showListPicker, setShowListPicker] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const listPickerRef = useRef(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [cName, setCName] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cType, setCType] = useState("static");
  const [cFilters, setCFilters] = useState([]);

  // Import
  const [showImport, setShowImport] = useState(false);
  const [impStep, setImpStep] = useState(1); // 1=upload, 2=map, 3=preview, 4=importing, 5=done
  const [impData, setImpData] = useState(null);
  const [impMap, setImpMap] = useState([]);
  const [impTarget, setImpTarget] = useState("");
  // Holds the new-list name when impTarget === "__create__". Pre-created at
  // the top of doImport so the new list exists before the post-import
  // addToList call. Reset alongside impTarget in resetImp so cancelling and
  // restarting the import doesn't leak a stale name into the next session.
  const [impNewListName, setImpNewListName] = useState("");
  const [impProg, setImpProg] = useState({ cur: 0, tot: 0, ok: 0, dup: 0, skip: 0, err: 0 });

  // Export
  const [showExport, setShowExport] = useState(false);
  const [expSel, setExpSel] = useState(EXP_FIELDS.map(f => f.id));

  // Add to list
  const [showAddTo, setShowAddTo] = useState(false);

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ═══ MULTI-ORG: Reset ALL state on org change ═══
  useEffect(() => {
    setLists([]);
    setMembers({});
    setActiveList(null);
    setSelected(new Set());
    setSearch("");
    resetImp();
    if (orgId) loadLists();
  }, [orgId]);

  // Close list picker on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (listPickerRef.current && !listPickerRef.current.contains(e.target)) setShowListPicker(false);
    };
    if (showListPicker) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showListPicker]);

  // ═══ LOAD LISTS ═══
  // Both queries paginate via fetchAllPaginated (PostgREST silently caps any
  // unbounded .select() at db-max-rows=1000). The members preload saturates
  // fastest — it pulls every static-list membership across all lists in the
  // org, so an org with 2 lists of 800 contacts each blows the cap.
  // .order("added_at") is required on the members query: paginated .range()
  // calls need a stable ordering so page boundaries don't shift between
  // round-trips.
  const loadLists = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await fetchAllPaginated(() =>
        supabase
          .from("contact_lists")
          .select("*")
          .eq("org_id", orgId)
          .order("created_at", { ascending: true })
      );
      setLists(data || []);
      // Preload member counts for all static lists
      const staticIds = (data || []).filter(l => l.type === "static").map(l => l.id);
      if (staticIds.length > 0) {
        const mData = await fetchAllPaginated(() =>
          supabase
            .from("contact_list_members")
            .select("list_id, contact_id")
            .in("list_id", staticIds)
            .order("added_at", { ascending: true })
        );
        if (mData) {
          const map = {};
          for (const row of mData) {
            if (!map[row.list_id]) map[row.list_id] = [];
            map[row.list_id].push(row.contact_id);
          }
          setMembers(map);
        }
      }
    } catch (e) {
      console.warn("[Lists] Load failed:", e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // ═══ LOAD MEMBERS (for single list refresh) ═══
  // Paginated to bypass PostgREST's 1000-row cap. Fired both on user
  // navigation into a list AND after every addToList/removeFrom call —
  // so when an import bulk-adds 1800 contacts to a list, this is the
  // refresh that decides whether the user sees the full set. Without
  // pagination it returned 1000 even though all 1800 were in the DB.
  // .order("added_at") required for stable .range() page boundaries.
  const loadMembers = useCallback(async (lid) => {
    try {
      const data = await fetchAllPaginated(() =>
        supabase
          .from("contact_list_members")
          .select("contact_id")
          .eq("list_id", lid)
          .order("added_at", { ascending: true })
      );
      setMembers(p => ({ ...p, [lid]: (data || []).map(r => r.contact_id) }));
    } catch (e) {
      console.warn("[Lists] Load members:", e.message);
    }
  }, []);

  // ── Contacts for a given list ──
  const getLC = useCallback((list) => {
    if (!list) return contacts;
    if (list.type === "smart") return contacts.filter(c => (list.filters || []).every(f => matchesFilter(c, f)));
    return contacts.filter(c => (members[list.id] || []).includes(c.id));
  }, [contacts, members]);

  // ── Filtered display contacts ──
  const display = useMemo(() => {
    const base = activeList ? getLC(activeList) : contacts;
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(c => ((c.fn || "") + " " + (c.ln || "") + " " + (c.email || "") + " " + (c.company || c.co || "")).toLowerCase().includes(q));
  }, [contacts, activeList, search, getLC]);

  // ═══ CREATE LIST ═══
  // Pagination
  const totalPages = Math.max(1, Math.ceil(display.length / pageSize));
  const paginatedDisplay = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return display.slice(start, start + pageSize);
  }, [display, currentPage, pageSize]);

  const doCreate = async () => {
    const name = cName.trim();
    if (!name || name.length < 1 || !orgId) return;
    try {
      const { data, error } = await supabase.from("contact_lists").insert({
        org_id: orgId,
        created_by: currentUserId || null,
        name,
        type: cType,
        description: cDesc.trim() || null,
        filters: cType === "smart" ? cFilters.filter(f => f.value) : [],
      }).select().single();
      if (error) throw error;
      setLists(p => [...p, data]);
      setActiveList(data);
    } catch (e) {
      console.warn("[Lists] Create failed:", e.message);
    }
    setShowCreate(false); setCName(""); setCDesc(""); setCType("static"); setCFilters([]);
  };

  // ═══ DELETE LIST (with confirmation) ═══
  const doDelete = async (id) => {
    try {
      const { error } = await supabase.from("contact_lists").delete().eq("id", id).eq("org_id", orgId);
      if (error) throw error;
      setLists(p => p.filter(l => l.id !== id));
      setMembers(p => { const n = { ...p }; delete n[id]; return n; });
      if (activeList?.id === id) setActiveList(null);
    } catch (e) {
      console.warn("[Lists] Delete failed:", e.message);
    }
    setConfirmDelete(null);
  };

  // ═══ ADD CONTACTS TO LIST ═══
  const addToList = async (lid, cids) => {
    if (!cids.length || !lid) return;
    // Verify list belongs to current org
    const list = lists.find(l => l.id === lid);
    if (!list) return;
    try {
      const rows = cids.map(c => ({ list_id: lid, contact_id: c, added_by: currentUserId || null }));
      const { error } = await supabase
        .from("contact_list_members")
        .upsert(rows, { onConflict: "list_id,contact_id" });
      if (error) throw error;
      await loadMembers(lid);
    } catch (e) {
      console.warn("[Lists] Add members:", e.message);
    }
    setSelected(new Set());
    setShowAddTo(false);
  };

  // ═══ REMOVE CONTACT FROM LIST ═══
  const removeFrom = async (cid) => {
    if (!activeList || activeList.type !== "static") return;
    try {
      const { error } = await supabase
        .from("contact_list_members")
        .delete()
        .eq("list_id", activeList.id)
        .eq("contact_id", cid);
      if (error) throw error;
      setMembers(p => ({ ...p, [activeList.id]: (p[activeList.id] || []).filter(id => id !== cid) }));
    } catch (e) {
      console.warn("[Lists] Remove member:", e.message);
    }
  };

  // ═══ IMPORT: FILE UPLOAD ═══
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { alert("File too large. Maximum 20MB."); return; }
    const r = new FileReader();
    r.onload = (ev) => {
      const { headers, rows } = parseCSV(ev.target.result);
      if (!headers.length) { alert("Could not parse CSV. Make sure it has column headers."); return; }
      if (rows.length > MAX_IMPORT_ROWS) { alert(`Too many rows (${rows.length}). Maximum is ${MAX_IMPORT_ROWS}.`); return; }
      if (rows.length === 0) { alert("CSV has headers but no data rows."); return; }
      setImpData({ headers, rows });
      // Shared scored mapper. First 20 rows feed content sniffing so
      // "Company Size" (values like "11-50") loses to a real Company column
      // (values like "Acme Corp") for the company field. Bipartite assign
      // also guarantees no two columns claim the same field.
      const autoMapped = autoMapColumns(headers, rows.slice(0, 20), ccf);
      setImpMap(headers.map((_, i) => autoMapped[i] || "skip"));
      setImpStep(2);
    };
    r.onerror = () => { alert("Failed to read file."); };
    r.readAsText(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ═══ IMPORT: EXECUTE ═══
  const doImport = async () => {
    if (!impData || importingRef.current) return;
    // Validate at least one non-skip mapping
    const hasMapped = impMap.some(m => m !== "skip");
    if (!hasMapped) { alert("Please map at least one column before importing."); return; }

    // Pre-create list if user chose "+ Create new list…". Done before any
    // contact inserts run so that if list creation fails we abort cleanly
    // with no orphan contacts. The resolved id (real list id, or whatever
    // the user picked) is what flows into the post-import addToList call.
    let resolvedTarget = impTarget;
    if (impTarget === "__create__") {
      const name = (impNewListName || "").trim();
      if (!name) {
        alert("Please enter a name for the new list.");
        return;
      }
      try {
        const { data, error } = await supabase.from("contact_lists").insert({
          org_id: orgId,
          created_by: currentUserId || null,
          name,
          type: "static",
        }).select().single();
        if (error) throw error;
        setLists(p => [...p, data]);
        resolvedTarget = data.id;
      } catch (e) {
        alert("Failed to create list: " + e.message);
        return;
      }
    }

    importingRef.current = true;
    setImpStep(4);
    const tot = impData.rows.length;
    let ok = 0, dup = 0, skip = 0, err = 0;
    const ids = [];
    setImpProg({ cur: 0, tot, ok: 0, dup: 0, skip: 0, err: 0 });

    // ── Pre-pass: extract company column values from every row, then run
    // ONE batched find-or-create against the companies table. The returned
    // map gives O(1) per-row lookup of company_id by normalized name.
    const companyColIdx = impMap.findIndex(m => m === "company");
    const companyNames = companyColIdx >= 0
      ? impData.rows.map(r => r[companyColIdx]).filter(Boolean)
      : [];
    const companyMap = await store.findOrCreateCompaniesBulk(companyNames);

    for (let i = 0; i < tot; i++) {
      const row = impData.rows[i];
      const cd = {};
      const cp = {};

      impMap.forEach((fid, j) => {
        if (fid === "skip" || !row[j]) return;
        if (fid.startsWith("custom_")) { cp[fid.replace("custom_", "")] = row[j]; return; }
        if (fid === "phone") { cd.ph = row[j]; return; }
        if (fid === "company") { cd.co = row[j]; return; }
        if (fid === "tags") { cd.tags = row[j].split(/[;,]/).map(t => t.trim()).filter(Boolean); return; }
        cd[fid] = row[j];
      });

      // Resolve company_id from the pre-pass map (case-insensitive).
      // Empty/whitespace company column → company_id stays undefined → null in DB.
      if (cd.co) {
        const normalized = cd.co.trim().toLowerCase();
        if (companyMap[normalized]) cd.company_id = companyMap[normalized];
      }

      // Skip rows with no name AND no email
      if (!cd.fn && !cd.email) {
        skip++;
        setImpProg({ cur: i + 1, tot, ok, dup, skip, err });
        continue;
      }
      if (!cd.fn) cd.fn = (cd.email || "").split("@")[0] || "Unknown";
      if (!cd.ln) cd.ln = "";

      // Duplicate check by email (case-insensitive)
      if (cd.email) {
        const existing = contacts.find(c => c.email && c.email.toLowerCase() === cd.email.toLowerCase());
        if (existing) {
          // Update custom props on existing duplicate
          if (Object.keys(cp).length > 0) {
            try { await updateContact(existing.id, cp); } catch {}
          }
          dup++;
          ids.push(existing.id);
          setImpProg({ cur: i + 1, tot, ok, dup, skip, err });
          continue;
        }
      }

      // Merge custom props and create.
      // cp is keyed by the field's actual id (e.g. "custom_1714000000000").
      // Custom values must land in cd.properties (the JSONB column) — NOT at
      // the top level of cd, because dbInsertContact only writes the explicit
      // first-class columns plus `properties: c.properties || {}` and ignores
      // any other top-level keys. (dbUpdateContact does collect top-level
      // custom keys into properties, which is why the duplicate-update branch
      // above works correctly with `cp` flattened — but the create branch
      // needs the values nested up front.)
      cd.properties = { ...(cd.properties || {}), ...cp };
      // _skipActivity suppresses the per-contact 'contact_created'
      // activity that dbInsertContact would normally write. We emit
      // one batch 'import' activity at the end instead.
      cd._skipActivity = true;
      try {
        const res = await createContact(cd);
        if (res) { ok++; ids.push(res.id); }
        else err++;
      } catch {
        err++;
      }
      setImpProg({ cur: i + 1, tot, ok, dup, skip, err });
    }

    // Add to target list if selected (resolvedTarget is the pre-created
    // new-list id when impTarget was "__create__", otherwise === impTarget).
    if (resolvedTarget && ids.length > 0) {
      await addToList(resolvedTarget, ids);
    }

    // Single batch activity for the import. Per-contact rows would
    // flood the importer's My Activity panel; one summary row keeps
    // it readable while still giving them credit for the action.
    if (ok > 0) {
      const targetListName = (resolvedTarget && lists.find(l => l.id === resolvedTarget)?.name) || null;
      try {
        await store.addActivity({
          type: "import",
          title: `Imported ${ok} contact${ok === 1 ? "" : "s"}`,
          notes: `via CSV${targetListName ? ` → ${targetListName}` : ""}`,
        });
      } catch (e) { console.warn("[Lists] import activity write failed:", e?.message); }
    }

    setImpStep(5);
    setImpProg({ cur: tot, tot, ok, dup, skip, err });
    importingRef.current = false;
  };

  // ═══ EXPORT ═══
  const doExport = () => {
    const fields = allExp.filter(f => expSel.includes(f.id));
    if (!fields.length) { alert("Please select at least one field to export."); return; }
    if (!display.length) { alert("No contacts to export."); return; }
    const listName = activeList ? activeList.name.replace(/[^a-zA-Z0-9]/g, "-") : "all-contacts";
    downloadCSV(display, fields, `${listName}-${new Date().toISOString().slice(0, 10)}.csv`);
    setShowExport(false);
  };

  // ── Reset import state ──
  const resetImp = () => {
    setShowImport(false); setImpStep(1); setImpData(null); setImpMap([]); setImpTarget(""); setImpNewListName("");
    setImpProg({ cur: 0, tot: 0, ok: 0, dup: 0, skip: 0, err: 0 });
    importingRef.current = false;
  };

  // ── Select helpers ──
  const toggleAll = () => { selected.size === display.length ? setSelected(new Set()) : setSelected(new Set(display.map(c => c.id))); };
  const toggleOne = (id) => { const n = new Set(selected); selected.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const statics = lists.filter(l => l.type === "static");
  const smarts = lists.filter(l => l.type === "smart");

  // ═══ RENDER ═══
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent", fontFamily: "inherit" }}>

      {/* ═══ MAIN ═══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header bar */}
        <div style={{ padding: isMobile ? "10px 12px 0" : "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div ref={listPickerRef} style={{ position: "relative" }}>
              <button onClick={() => { setShowListPicker(!showListPicker); setListSearch(""); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showListPicker ? "rgba(255,255,255,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease" }} onMouseEnter={e => { if (!showListPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }} onMouseLeave={e => { if (!showListPicker) e.currentTarget.style.background = "transparent"; }}>
                <Icon name="list" size={14} color="#FAFAFA" />
                <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{activeList?.name || "All Contacts"}</span>
                <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
              </button>

              {/* List Picker Popup */}
              {showListPicker && <div className="cf-dropdown-popup" onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 280, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)", zIndex: 99999, overflow: "hidden" }}>
                <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                  <input autoFocus value={listSearch} onChange={e => setListSearch(e.target.value)} placeholder="Search lists..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto", padding: "6px 8px" }}>
                  {/* All Contacts */}
                  {("all contacts".includes(listSearch.toLowerCase()) || !listSearch) && <button onClick={() => { setActiveList(null); setSelected(new Set()); setShowListPicker(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: !activeList ? T.blue : "rgba(255,255,255,0.04)", color: !activeList ? "#fff" : "#FAFAFA", transition: "all .15s cubic-bezier(.16,1,.3,1)" }} onMouseEnter={e => { if (activeList) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (activeList) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                    <Icon name="user" size={13} color={!activeList ? "#fff" : "#FAFAFA"} />
                    <span style={{ fontSize: 13, fontWeight: !activeList ? 600 : 400, flex: 1 }}>All Contacts</span>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{contacts.length}</span>
                  </button>}

                  {/* Static Lists */}
                  {statics.filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase())).length > 0 && <div style={{ padding: "12px 10px 4px" }}><span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".08em", opacity: 0.4 }}>STATIC LISTS</span></div>}
                  {statics.filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase())).map(l => <button key={l.id} onClick={() => { setActiveList(l); setSelected(new Set()); setShowListPicker(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: activeList?.id === l.id ? T.blue : "rgba(255,255,255,0.04)", color: activeList?.id === l.id ? "#fff" : "#FAFAFA", transition: "all .15s cubic-bezier(.16,1,.3,1)" }} onMouseEnter={e => { if (activeList?.id !== l.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (activeList?.id !== l.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                    <Icon name="list" size={13} color={activeList?.id === l.id ? "#fff" : "#FAFAFA"} />
                    <span style={{ fontSize: 13, fontWeight: activeList?.id === l.id ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{(members[l.id] || []).length}</span>
                  </button>)}

                  {/* Smart Segments */}
                  {smarts.filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase())).length > 0 && <div style={{ padding: "12px 10px 4px" }}><span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".08em", opacity: 0.4 }}>SMART SEGMENTS</span></div>}
                  {smarts.filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase())).map(l => <button key={l.id} onClick={() => { setActiveList(l); setSelected(new Set()); setShowListPicker(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: activeList?.id === l.id ? "#8B5CF6" : "rgba(255,255,255,0.04)", color: activeList?.id === l.id ? "#fff" : "#FAFAFA", transition: "all .15s cubic-bezier(.16,1,.3,1)" }} onMouseEnter={e => { if (activeList?.id !== l.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }} onMouseLeave={e => { if (activeList?.id !== l.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                    <Icon name="zap" size={13} color={activeList?.id === l.id ? "#fff" : "#8B5CF6"} />
                    <span style={{ fontSize: 13, fontWeight: activeList?.id === l.id ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{getLC(l).length}</span>
                  </button>)}

                  {/* New List button */}
                  <div style={{ borderTop: "1px solid rgba(217,219,221,0.08)", marginTop: 6, paddingTop: 6 }}>
                    <button onClick={() => { setShowCreate(true); setShowListPicker(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: "transparent", color: "#4059AD", transition: "background .15s" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 13, fontWeight: 400 }}>+ New List</span>
                    </button>
                  </div>
                </div>
              </div>}
            </div>

            {activeList?.type === "smart" && <Tag color="#8B5CF6">Smart</Tag>}
            {activeList?.description && <span style={{ fontSize: 11, color: "#FAFAFA", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeList.description}</span>}
            <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{display.length} contacts</span>
          </div>
          {/* No right-side buttons on view picker row — just like People has List/Board toggle here, we leave it clean */}
        </div>

        {/* ─── HEADER BAR — search + buttons, matches People layout ─── */}
        <div style={{ display: "flex", gap: 6, padding: "10px 24px 14px", alignItems: "center", flexShrink: 0 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: 180 }} />
          <div style={{ flex: 1 }} />
          {selected.size > 0 && (
            <>
              <button onClick={() => setShowAddTo(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
                Add to List ({selected.size})
              </button>
              <button onClick={() => setSelected(new Set())} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
                Clear
              </button>
            </>
          )}
          <HBtn icon="upload" label="Import" click={() => setShowImport(true)} />
          <HBtn icon="download" label="Export" click={() => setShowExport(true)} />
          {activeList && <button onClick={() => setConfirmDelete(activeList.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(220,38,38,0.15)", background: "transparent", color: "#DC2626", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>}
        </div>

        {/* Contacts table */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 24px" }}>
          {/* tableLayout: fixed + explicit per-column widths so long values
              (paragraph-length names, multi-line company strings) clip with
              ellipsis instead of inflating row heights. truncStyle is applied
              to every text-bearing <td> below. Display-only — DB unchanged. */}
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead><tr style={{ borderBottom: "1px solid " + T.bdr, position: "sticky", top: 0, background: "#2C2E33", zIndex: 2 }}>
              <th style={{ padding: "10px 12px", textAlign: "left", width: 36 }}>
                <Checkbox checked={selected.size === display.length && display.length > 0} onChange={toggleAll} />
              </th>
              {[
                { label: "CONTACT", w: 220, align: "left" },
                { label: "EMAIL", w: 220, align: "center" },
                { label: "COMPANY", w: 180, align: "center" },
                { label: "SCORE", w: 100, align: "center" },
                { label: "STATUS", w: 120, align: "center" },
                { label: "SOURCE", w: 120, align: "center" },
              ].map(h => (
                <th key={h.label} style={{ padding: "10px 12px", textAlign: h.align, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", whiteSpace: "nowrap", userSelect: "none", width: h.w }}>{h.label}</th>
              ))}
              {activeList?.type === "static" && <th style={{ width: 50 }} />}
            </tr></thead>
            <tbody>
              {paginatedDisplay.map(c => {
                const sel = selected.has(c.id);
                const cellStyle = { padding: "8px 12px", cursor: "pointer", textAlign: "center" };
                // Per-column widths mirror the <th> array above. Body cells
                // need explicit width+maxWidth under tableLayout: fixed so
                // truncStyle has something to clip against.
                const fullName = `${c.fn || ""} ${c.ln || ""}`.trim();
                const emailV = c.email || "—";
                const companyV = c.company || c.co || "—";
                return (
                  <tr key={c.id} style={{ borderBottom: "1px solid " + T.bdr, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "8px 12px", width: 36 }} onClick={e => e.stopPropagation()}>
                      <Checkbox checked={sel} onChange={() => toggleOne(c.id)} />
                    </td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 220, maxWidth: 220, textAlign: "left" }} onClick={() => onSelectContact?.(c.id)} title={fullName}>
                      {/* Outer + inner divs each apply truncStyle so name and
                          title-secondary clip independently on their own line. */}
                      <div style={{ ...truncStyle }}>
                        <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, ...truncStyle }}>{c.fn} {c.ln}</div>
                        {c.title && <div style={{ fontSize: 9, color: T.tx3, ...truncStyle }} title={c.title}>{c.title}</div>}
                      </div>
                    </td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 220, maxWidth: 220, fontSize: 11, color: T.tx2 }} onClick={() => onSelectContact?.(c.id)} title={emailV}>{emailV}</td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 180, maxWidth: 180, fontSize: 11, color: T.tx2 }} onClick={() => onSelectContact?.(c.id)} title={companyV}>{companyV}</td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 100, maxWidth: 100, fontSize: 11, fontWeight: 400, color: "#FAFAFA" }} onClick={() => onSelectContact?.(c.id)}>{c.score || "—"}</td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 120, maxWidth: 120, fontSize: 11, color: T.txt, textTransform: "capitalize" }} onClick={() => onSelectContact?.(c.id)}>{c.status || "—"}</td>
                    <td style={{ ...cellStyle, ...truncStyle, width: 120, maxWidth: 120, fontSize: 11, color: T.tx3 }} onClick={() => onSelectContact?.(c.id)}>{c.source || "—"}</td>
                    {activeList?.type === "static" && (
                      <td style={{ padding: "8px 8px", textAlign: "center", width: 50 }}>
                        <button onClick={e => { e.stopPropagation(); removeFrom(c.id); }} style={{ fontSize: 11, color: T.tx3, border: "none", background: "transparent", cursor: "pointer" }} title="Remove from list">✕</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {display.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12 }}>
                  {loading ? "Loading..." : activeList ? "No contacts in this list" : search ? "No matches" : "No contacts yet"}
                </td></tr>
              )}
            </tbody>
          </table>
          {/* Pagination */}
          {display.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 11, color: T.tx3 }}>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, display.length)} of {display.length}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {[20, 50, 100].map(size => (
                    <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none", background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent", color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>{size}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i; return (
                  <button key={pn} onClick={() => setCurrentPage(pn)} style={{ width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit", background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent", color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer" }}>{pn}</button>
                ); })}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ CREATE MODAL ═══ */}
      {showCreate && <Mod close={() => setShowCreate(false)}>
        <MT>Create List</MT>
        <FL l="Name"><input value={cName} onChange={e => setCName(e.target.value)} placeholder="e.g. Q1 Prospects" autoFocus style={IS} onKeyDown={e => e.key === "Enter" && doCreate()} /></FL>
        <FL l="Description"><input value={cDesc} onChange={e => setCDesc(e.target.value)} placeholder="Optional" style={IS} /></FL>
        <FL l="Type">
          <div style={{ display: "flex", gap: 8 }}>
            {[{ id: "static", t: "Static List", d: "Manually add / remove" }, { id: "smart", t: "Smart Segment", d: "Auto-populate by filters" }].map(t => (
              <button key={t.id} onClick={() => setCType(t.id)} style={{ flex: 1, padding: "12px 14px", borderRadius: 8, textAlign: "left", fontFamily: "inherit", cursor: "pointer", border: cType === t.id ? "2px solid #4059AD" : "1px solid rgba(217,219,221,0.12)", background: cType === t.id ? "rgba(64,89,173,0.15)" : "rgba(255,255,255,0.04)" }}>
                <div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{t.t}</div>
                <div style={{ fontSize: 11, color: "rgba(250,250,250,0.5)", marginTop: 2 }}>{t.d}</div>
              </button>
            ))}
          </div>
        </FL>
        {cType === "smart" && <FL l="Filters">
          {cFilters.map((f, i) => {
            // allSegFields wins over SEG_MAP — it carries the override-aware options for Tier B fields
            const def = allSegFields.find(s => s.id === f.id) || SEG_MAP[f.id];
            return (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <Select value={f.id} onChange={e => { const n = [...cFilters]; n[i] = { id: e.target.value, value: "" }; setCFilters(n); }} style={SS}>{allSegFields.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</Select>
                {def?.type === "select" ? <Select value={f.value} onChange={e => { const n = [...cFilters]; n[i].value = e.target.value; setCFilters(n); }} style={{ ...SS, flex: 1 }}><option value="">Select...</option>{def.options.map(o => <option key={o} value={o}>{o}</option>)}</Select>
                : def?.type === "boolean" ? <Select value={f.value || "true"} onChange={e => { const n = [...cFilters]; n[i].value = e.target.value; setCFilters(n); }} style={{ ...SS, flex: 1 }}><option value="true">Yes</option><option value="false">No</option></Select>
                : <input value={f.value} onChange={e => { const n = [...cFilters]; n[i].value = e.target.value; setCFilters(n); }} type={def?.type === "number" ? "number" : def?.type === "date" ? "date" : "text"} placeholder="Value..." style={{ ...IS, flex: 1 }} />}
                <button onClick={() => setCFilters(cFilters.filter((_, j) => j !== i))} style={xBtn}>✕</button>
              </div>
            );
          })}
          <button onClick={() => setCFilters([...cFilters, { id: allSegFields[0].id, value: "" }])} style={linkBtn}>+ Add filter</button>
        </FL>}
        <MA cancel={() => setShowCreate(false)} ok={doCreate} okL="Create" />
      </Mod>}

      {/* ═══ IMPORT MODAL ═══ */}
      {showImport && <Mod close={impStep < 4 ? resetImp : undefined} w>
        <MT>Import Contacts</MT>

        {impStep === 1 && <>
          <div style={dropZone} onClick={() => fileRef.current?.click()}>
            <Icon name="upload" size={28} color="#9CA3AF" />
            <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA", marginTop: 12 }}>Click to upload CSV</div>
            <div style={{ fontSize: 12, color: "#FAFAFA", marginTop: 4 }}>.csv with column headers — max {MAX_IMPORT_ROWS.toLocaleString()} rows, 20MB</div>
          </div>
          <input type="file" ref={fileRef} accept=".csv" onChange={onFile} style={{ display: "none" }} />
          <FL l="Add to list (optional)">
            <div style={{ display: "flex", gap: 8 }}>
              <Select value={impTarget} onChange={e => setImpTarget(e.target.value)} style={{ ...SS, flex: 1 }}>
                <option value="">None</option>
                <option value="__create__">+ Create new list…</option>
                <option disabled>──────────</option>
                {statics.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
              {impTarget === "__create__" && (
                <input
                  value={impNewListName}
                  onChange={e => setImpNewListName(e.target.value)}
                  placeholder="New list name"
                  style={{ ...IS, flex: 1 }}
                  autoFocus
                />
              )}
            </div>
          </FL>
        </>}

        {impStep === 2 && impData && (() => {
          // Each non-skip field id can be claimed by at most one column.
          // The current column's own selection is excluded from the set so
          // it stays selectable in its own dropdown.
          const usedFields = new Set(impMap.filter(v => v && v !== "skip"));
          return (
          <>
          <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 12 }}>Map columns — {impData.rows.length.toLocaleString()} rows found</div>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {impData.headers.map((h, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: "#FAFAFA", width: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={h}>{h}</span>
                <span style={{ color: "#FAFAFA" }}>→</span>
                <Select value={impMap[i]} onChange={e => { const n = [...impMap]; n[i] = e.target.value; setImpMap(n); }} style={{ ...SS, flex: 1 }}>
                  {allImp.map(f => {
                    const isUsedElsewhere = f.id !== "skip" && f.id !== impMap[i] && usedFields.has(f.id);
                    return (
                      <option key={f.id} value={f.id} disabled={isUsedElsewhere}>
                        {f.label}{isUsedElsewhere ? " (already used)" : ""}
                      </option>
                    );
                  })}
                </Select>
                {impData.rows[0]?.[i] && <span style={{ fontSize: 11, color: "#FAFAFA", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={impData.rows[0][i]}>e.g. {impData.rows[0][i]}</span>}
              </div>
            ))}
          </div>
          <MA cancel={() => { setImpStep(1); setImpData(null); }} cancelL="Back" ok={() => setImpStep(3)} okL="Preview" />
        </>
        );
        })()}

        {impStep === 3 && impData && <>
          <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 8 }}>Preview — first 5 rows</div>
          <div style={{ overflowX: "auto", marginBottom: 14, borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead><tr>{impMap.map((m, i) => m !== "skip" ? <th key={i} style={{ padding: "6px 8px", textAlign: "left", fontWeight: 400, color: "#FAFAFA", borderBottom: "1px solid rgba(217,219,221,0.10)", background: "rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>{allImp.find(f => f.id === m)?.label || m}</th> : null)}</tr></thead>
              <tbody>{impData.rows.slice(0, 5).map((r, ri) => <tr key={ri}>{impMap.map((m, i) => m !== "skip" ? <td key={i} style={{ padding: "6px 8px", borderBottom: "1px solid rgba(217,219,221,0.08)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r[i] || "—"}</td> : null)}</tr>)}</tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: "#FAFAFA", marginBottom: 14 }}>
            Ready to import <strong>{impData.rows.length.toLocaleString()}</strong> contacts into <strong>{orgId ? "this organization" : "—"}</strong>. Duplicates (email match) will have custom fields updated.
          </div>
          <MA cancel={() => setImpStep(2)} cancelL="Back" ok={doImport} okL={`Import ${impData.rows.length.toLocaleString()} Contacts`} okC="#16A34A" />
        </>}

        {impStep === 4 && <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 16 }}>Importing contacts...</div>
          <div style={{ width: "100%", height: 8, borderRadius: 4, background: "rgba(217,219,221,0.12)", marginBottom: 16, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 4, background: "#4059AD", width: impProg.tot ? `${(impProg.cur / impProg.tot) * 100}%` : "0%", transition: "width 0.3s" }} />
          </div>
          <div style={{ fontSize: 28, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>{impProg.cur.toLocaleString()} / {impProg.tot.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: "#FAFAFA", marginBottom: 16 }}>{impProg.tot ? `${Math.round((impProg.cur / impProg.tot) * 100)}% complete` : "Starting..."}</div>
          <ProgStats p={impProg} />
          <div style={{ fontSize: 11, color: "#FAFAFA", marginTop: 16 }}>Do not close this window</div>
        </div>}

        {impStep === 5 && <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(46,139,87,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#2E8B57" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
          <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 12 }}>Import Complete</div>
          <ProgStats p={impProg} />
          {impProg.err > 0 && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 8 }}>{impProg.err} row{impProg.err > 1 ? "s" : ""} failed to save</div>}
          <button onClick={resetImp} style={{ marginTop: 20, padding: "10px 28px", borderRadius: 8, border: "none", background: "#4059AD", color: "#fff", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
        </div>}
      </Mod>}

      {/* ═══ EXPORT MODAL ═══ */}
      {showExport && <Mod close={() => setShowExport(false)}>
        <MT>Export Contacts</MT>
        <div style={{ fontSize: 12, color: "#FAFAFA", marginBottom: 16 }}>{display.length.toLocaleString()} contacts will be exported{activeList ? ` from "${activeList.name}"` : ""}</div>
        <FL l="Include fields:">
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <button onClick={() => setExpSel(allExp.map(f => f.id))} style={linkBtn}>Select all</button>
            <button onClick={() => setExpSel([])} style={linkBtn}>Clear all</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            {allExp.map(f => <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", padding: "4px 0" }}><Checkbox checked={expSel.includes(f.id)} onChange={() => setExpSel(p => p.includes(f.id) ? p.filter(x => x !== f.id) : [...p, f.id])} />{f.label}</label>)}
          </div>
        </FL>
        <MA cancel={() => setShowExport(false)} ok={doExport} okL="Download CSV" />
      </Mod>}

      {/* ═══ ADD TO LIST MODAL ═══ */}
      {showAddTo && <Mod close={() => setShowAddTo(false)}>
        <MT>Add {selected.size} contact{selected.size > 1 ? "s" : ""} to list</MT>
        {statics.length === 0 ? (
          <div style={{ fontSize: 12, color: "#FAFAFA", padding: 20, textAlign: "center" }}>No static lists yet. Create one first.</div>
        ) : statics.map(l => (
          <button key={l.id} onClick={() => addToList(l.id, [...selected])} style={listPickBtn}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(64,89,173,0.05)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <Icon name="list" size={13} color="#6B7280" />
            <span style={{ flex: 1, fontSize: 13, color: "#FAFAFA" }}>{l.name}</span>
            <span style={{ fontSize: 11, color: "#FAFAFA" }}>{(members[l.id] || []).length}</span>
          </button>
        ))}
      </Mod>}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {confirmDelete && <Mod close={() => setConfirmDelete(null)}>
        <MT>Delete List</MT>
        <div style={{ fontSize: 13, color: "#FAFAFA", marginBottom: 20 }}>
          Are you sure you want to delete "<strong>{lists.find(l => l.id === confirmDelete)?.name}</strong>"?
          {lists.find(l => l.id === confirmDelete)?.type === "static"
            ? " This will remove the list but won't delete the contacts themselves."
            : " This will remove the segment definition."}
        </div>
        <MA cancel={() => setConfirmDelete(null)} ok={() => doDelete(confirmDelete)} okL="Delete" okC="#DC2626" cancelL="Keep" />
      </Mod>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

function SBI({ label, icon, n, on, click, ic }) {
  const [h, setH] = useState(false);
  return <button onClick={click} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: on ? "rgba(64,89,173,0.10)" : h ? "rgba(217,219,221,0.08)" : "transparent", transition: "background 0.1s" }}>
    <Icon name={icon} size={13} color={on ? (ic || "#FAFAFA") : "#6B7280"} />
    <span style={{ fontSize: 13, color: on ? (ic || "#FAFAFA") : "#6B7280", fontWeight: on ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ fontSize: 11, color: "#FAFAFA", minWidth: 16, textAlign: "right" }}>{n}</span>
  </button>;
}

function SL({ text }) { return <div style={{ padding: "14px 8px 6px" }}><span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".08em" }}>{text}</span></div>; }

function SP({ s }) {
  const bg = s === "active" ? "rgba(22,163,74,0.08)" : s === "nurture" ? "rgba(217,119,6,0.08)" : "rgba(0,0,0,0.20)";
  const c = s === "active" ? "#16A34A" : s === "nurture" ? "#D97706" : "#9CA3AF";
  return <span style={{ fontSize: 11, fontWeight: 400, padding: "2px 8px", borderRadius: 6, background: bg, color: c }}>{s || "—"}</span>;
}

function HBtn({ icon, label, click }) {
  return <button onClick={click} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}><Icon name={icon} size={11} color="#6B7280" /> {label}</button>;
}

function SmBtn({ label, onClick, blue, muted }) {
  return <button onClick={onClick} style={{ padding: "4px 12px", borderRadius: 6, border: blue ? "1px solid rgba(64,89,173,0.15)" : muted ? "none" : "1px solid rgba(217,219,221,0.12)", background: blue ? "rgba(64,89,173,0.05)" : "transparent", color: blue ? "#FAFAFA" : muted ? "#9CA3AF" : "#6B7280", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>;
}

function Tag({ children, color }) {
  return <span style={{ fontSize: 10, fontWeight: 400, color, padding: "2px 8px", borderRadius: 6, background: color + "14" }}>{children}</span>;
}

function Av({ c }) {
  return null;
}

function ProgStats({ p }) {
  return <div style={{ display: "flex", justifyContent: "center", gap: 24, fontSize: 12 }}>
    <div><span style={{ fontWeight: 400, color: "#16A34A" }}>{p.ok}</span> <span style={{ color: "#FAFAFA" }}>created</span></div>
    <div><span style={{ fontWeight: 400, color: "#D97706" }}>{p.dup}</span> <span style={{ color: "#FAFAFA" }}>duplicates</span></div>
    <div><span style={{ fontWeight: 400, color: "#FAFAFA" }}>{p.skip}</span> <span style={{ color: "#FAFAFA" }}>skipped</span></div>
    {p.err > 0 && <div><span style={{ fontWeight: 400, color: "#DC2626" }}>{p.err}</span> <span style={{ color: "#FAFAFA" }}>errors</span></div>}
  </div>;
}

function Mod({ children, close, w }) {
  return <div onClick={close || undefined} style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div onClick={e => e.stopPropagation()} style={{ width: w ? 580 : 440, maxHeight: "85vh", overflow: "auto", padding: 28, borderRadius: 8, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", boxShadow: "0 24px 64px rgba(0,0,0,0.50), 0 0 0 1px rgba(217,219,221,0.05)" }}>{children}</div>
  </div>;
}

function MT({ children }) { return <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 18 }}>{children}</div>; }
function FL({ l, children }) { return <div style={{ marginBottom: 14 }}><div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA", marginBottom: 6 }}>{l}</div>{children}</div>; }
function MA({ cancel, ok, cancelL, okL, okC }) {
  return <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
    <button onClick={cancel} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>{cancelL || "Cancel"}</button>
    <button onClick={ok} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: "none", background: okC || "#4059AD", color: "#fff", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>{okL || "Confirm"}</button>
  </div>;
}

// ── Shared styles ──
const IS = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const SS = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", color: "#FAFAFA", outline: "none" };
const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 400, color: "#FAFAFA", letterSpacing: ".04em" };
const tdStyle = { padding: "10px 12px", color: "#FAFAFA" };
const searchInput = { padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: 180 };
const btnMini = { padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 400, color: "#FAFAFA", fontFamily: "inherit" };
const xBtn = { border: "none", background: "transparent", color: "#FAFAFA", cursor: "pointer", fontSize: 14, padding: "2px 4px" };
const linkBtn = { fontSize: 11, fontWeight: 400, color: "#FAFAFA", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", padding: "2px 0" };
const dropZone = { border: "2px dashed rgba(217,219,221,0.15)", borderRadius: 8, padding: 40, textAlign: "center", cursor: "pointer", marginBottom: 14 };
const listPickBtn = { display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", background: "transparent" };
